import styled from 'styled-components';

import { Chip, CircularProgress, Collapse, IconButton, Stack, Switch, alpha } from '@mui/material';
import { ReactNode } from 'react';
import { InfoOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, LinkOutlined } from '@mui/icons-material';
import { Tooltip } from '@common/Tooltip';
import { useTheme } from '../../styles/theme';

const CardContainer = styled.div<{ fullWidth?: boolean }>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line.light};
  white-space: pre-wrap;

  ${({ fullWidth }) => fullWidth && `flex: 1;`};

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
    font-family: 'Inter';
  }

  a {
    color: ${({ theme }) => theme.primary};
  }
`;

const CardHeader = styled.div<{ noBody?: boolean }>`
  display: flex;
  flex-direction: column;

  gap: 16px;
  padding: 16px;
  /* padding-bottom: ${({ noBody }) => (noBody ? '16px' : '8px')}; */

  h4 {
    color: ${({ theme }) => theme.text.standard};
    font-family: 'PPFormula';
    font-size: 14px;
    padding-top: 4px;
    font-weight: 600;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 28px;
    max-height: 28px;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 25px;
    color: ${({ theme }) => theme.text.standard};
    max-width: 25px;
  }
`;

const CardContent = styled.div<{ noDescription?: boolean; noTitle?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: ${({ noTitle, noDescription }) => (noTitle ? '16px' : noDescription ? '8px 16px 16px 16px' : '16px')};
  gap: 1.25rem;
  color: ${({ theme }) => theme.text.standard};
`;

const CardInfo = styled.div<{ noBody?: boolean; border?: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  padding: ${({ noBody }) => (noBody ? '0px 16px 16px 16px' : '0px 16px 0 16px')};
  gap: 1.25rem;

  border-bottom: ${({ border }) => (border ? '1px solid #00000014' : 'none')};

  ${({ theme, border }) =>
    border &&
    `
    border-bottom: 1px solid ${theme.line.light};
    padding-bottom: 16px;
  `};

  p {
    font-size: 14px;
    font-family: 'Inter';
    margin-bottom: 4px;
  }
`;

const CardActions = styled.div`
  border-top: 1px solid ${({ theme }) => theme.line.light};
  padding: 16px;
`;

interface Props {
  // the title of the card
  title?: string;
  // this is the description placed below the title if set
  description?: string;
  // true if you want to add a border between the description and the body
  descriptionBorder?: boolean;
  // the icon to be displayed beside the title
  icon?: ReactNode;
  // a string if set will display a tooltip icon to the top right
  tooltip?: string;
  // a string if set will display a link icon in the top right that redirects to link when clicked
  link?: string;
  // the content in the center of the card, is hidden if not set
  body?: ReactNode;
  // the area beneath content which has action buttons, if hidden not set
  actions?: ReactNode;
  // will show an arrow in the top right representing the collapsed state, will collapse if clicked
  collapsed?: boolean;
  // needs to be set if collapsed is set, will toggle the collapsed state
  setCollapsed?: React.Dispatch<boolean>;
  // will show a loading spinner in the top right if set to true
  loading?: boolean;
  // sets the state of the enabled switch
  enabled?: boolean;
  // change the enabled state, renders a switch if set
  setEnabled?: React.Dispatch<boolean>;
  // ensures the width takes up entire available space
  fullWidth?: boolean;
  // when given a string value, will show a Chip with the value in the top right of the card
  chip?: string;
}

export const Card: React.FC<Props> = ({
  title,
  description,
  descriptionBorder,
  icon,
  loading,
  link,
  tooltip,
  body,
  actions,
  collapsed,
  setCollapsed,
  enabled,
  setEnabled,
  fullWidth,
  chip,
}) => {
  const theme = useTheme();

  return (
    <CardContainer fullWidth={fullWidth}>
      {title && (
        <CardHeader noBody={(!body && !link) || (setCollapsed && collapsed)}>
          <div className='row'>
            <div className='title'>
              {icon && <div className='icon'>{icon}</div>}
              <h4>{title}</h4>
            </div>
            <Stack direction='row' spacing={1} alignItems='center'>
              {setEnabled && (
                <Switch
                  size='small'
                  color='primary'
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              )}
              {link && (
                <IconButton
                  color='secondary'
                  sx={{ borderRadius: '6px' }}
                  size='small'
                  href={link}
                  rel='noreferrer'
                  target='_blank'
                >
                  <LinkOutlined sx={{ color: theme.text.standard, fontSize: '2.2ch !important' }} />
                </IconButton>
              )}
              {chip && <Chip size='small' label={chip} color='success' variant='outlined' />}
              {tooltip && (
                <Tooltip title={tooltip} placement='right' sx={{ maxWidth: '300px' }}>
                  <IconButton color='secondary' sx={{ borderRadius: '6px' }} size='small'>
                    <InfoOutlined sx={{ fontSize: '2ch !important', color: theme.text.standard }} />
                  </IconButton>
                </Tooltip>
              )}
              {setCollapsed && (
                <IconButton
                  size='small'
                  sx={{ padding: '2px', borderRadius: '6px' }}
                  onClick={() => setCollapsed(!collapsed)}
                >
                  {!collapsed ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
                </IconButton>
              )}
              {loading && <CircularProgress size={17} thickness={5} color='secondary' />}
            </Stack>
          </div>
        </CardHeader>
      )}
      <Collapse in={!collapsed} timeout='auto' unmountOnExit>
        <div>
          {description && (
            <CardInfo noBody={!body} border={descriptionBorder}>
              <p style={{ color: theme.text.subtext }}>{description}</p>
            </CardInfo>
          )}
          {body && (
            <CardContent noDescription={!description} noTitle={!title}>
              {body}
            </CardContent>
          )}
          {actions && <CardActions>{actions}</CardActions>}
        </div>
      </Collapse>
    </CardContainer>
  );
};
